import {
  Box,
  Button as MuiButton,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import UploadIcon from '@mui/icons-material/Upload';
import { CSVBoxButton } from '@csvbox/react';
import { Button } from '@baseComponents';
import { useUser } from '../../hooks/useUser';
import { useAgency } from '../../hooks/useAgency';
import { ImportedRow, ImportResult } from './types';
import InfoCallout from './InfoCallout';

interface CsvImportData {
  import_status: string;
  row_count: number;
  rows: ImportedRow[];
  ssv_fail: number;
}

interface UploadCsvStepProps {
  onImportSuccess: (result: ImportResult) => void;
  onClose: () => void;
}

const REQUIRED_FIELDS = ['First name', 'Last name', 'Email address', 'Designation', 'Role'];
const OPTIONAL_FIELDS = ['Phone Number', 'Badge number', 'Title', 'Rank', 'Armed permission (Y/N)'];
const BEFORE_UPLOAD_TIPS = [
  'List one person per line.',
  "Don't use the same email twice.",
  'Save the file as CSV (in Excel or Google Sheets: File → Save As / Download → CSV).',
  "It's okay to leave optional fields empty.",
  'For Armed permission, write Y for yes or N for no."',
];
const COMMON_ISSUES = [
  'Missing required information',
  'Duplicate email addresses',
  'Empty rows',
  'Files saved in a format other than CSV',
];

const UploadCsvStep: React.FC<UploadCsvStepProps> = ({ onImportSuccess, onClose }) => {
  const { user } = useUser();
  const { agency } = useAgency();

  const downloadCsvTemplate = () => {
    const link = document.createElement('a');
    link.href = '/user_import_spreadsheet_template.csv';
    link.download = 'user_import_spreadsheet_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Paper variant='outlined' sx={{ borderRadius: 2, p: 4 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>

        <Box>
          <Typography variant='h6' sx={{
            fontWeight: 600
          }}>Add your list of users</Typography>
          <Typography
            variant='body2'
            sx={{
              color: 'text.secondary',
              mt: 0.5
            }}>
            New to this? Download the template below, fill in each person's info, then upload it here. (A CSV is just a simple spreadsheet — you can make one in Excel or Google Sheets and save it as 'CSV'.)
          </Typography>
        </Box>

        <InfoCallout
          dashed={true}
          icon={<GridOnOutlinedIcon sx={{ mt: '2px', mr: 1 }} color='primary' />}
          title='Get a ready-made template'
          description='Already set up with the right columns — just fill in the blanks.'
          action={
            <MuiButton
              sx={{ textTransform: 'none', whiteSpace: 'nowrap', pl: 3, pr: 3, borderRadius: 2 }}
              variant='outlined'
              startIcon={<DownloadIcon />}
              onClick={downloadCsvTemplate}
            >
              Download Template
            </MuiButton>
          }
        />

        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography
              variant='s'
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                display: 'block'
              }}>
              REQUIRED INFORMATION
            </Typography>
            <Typography variant='caption' sx={{
              color: 'text.secondary'
            }}>
              Each user must include these fields.
            </Typography>
            <Box
              component='ul'
              sx={{ mt: 1.5, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 1 }}
            >
              {REQUIRED_FIELDS.map((field) => (
                <Box component='li' key={field} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TaskAltIcon color='primary' sx={{ fontSize: 18 }} />
                  <Typography variant='body2'>{field}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography
              variant='s'
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                display: 'block'
              }}>
              OPTIONAL INFORMATION
            </Typography>
            <Typography variant='caption' sx={{
              color: 'text.secondary'
            }}>
              Leave blank if you don't have it.
            </Typography>
            <Box component='ul' sx={{ mt: 1.5, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {OPTIONAL_FIELDS.map((field) => (
                <Typography component='li' variant='body2' key={field} sx={{
                  color: 'text.secondary'
                }}>
                  {field}
                </Typography>
              ))}
            </Box>
          </Grid>
        </Grid>

        <Paper variant='outlined' sx={{ borderRadius: 2, p: 2.5 }}>
          <Typography variant='s' sx={{
            fontWeight: 500
          }}>Before you upload</Typography>
          <Grid container spacing={1} sx={{ mt: 1 }}>
            {BEFORE_UPLOAD_TIPS.map((tip) => (
              <Grid size={{ xs: 12, sm: 6 }} key={tip} container>
                <Grid size={.5}>
                  <Typography variant='body2'>•</Typography>
                </Grid>
                <Grid size={11}>
                  <Typography variant='body2' sx={{
                    color: 'text.secondary'
                  }}> {tip}</Typography>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Paper>

        <InfoCallout
          icon={<InfoOutlinedIcon sx={{ mt: '2px', mr: 1 }} color='primary' />}
          title="Using your own Spreadsheet?"
          description={
            "That's okay — your column names don't need to match the template exactly. " +
            "In the next step, you'll match your columns to the right fields in the platform, " +
            "just make sure to save your file as CSV."
          }
        />

        <Box
          sx={{
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            p: 5,
            textAlign: 'center',
          }}
        >
          <UploadIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
          <Typography variant='s' sx={{
            fontWeight: 500
          }}>
            Ready to upload?
          </Typography>
          <Typography
            variant='body2'
            sx={{
              color: 'text.secondary',
              mt: 0.5
            }}>
            We'll guide you through mapping your columns next.
          </Typography>
          <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'center' }}>
            {user !== null && agency !== null &&
              <CSVBoxButton
                licenseKey={import.meta.env.VITE_CSVBOX_LICENSE_KEY}
                user={{
                  user_id: user.profile.id,
                  email: user.email,
                  agency_id: agency.id,
                }}
                onImport={(result: boolean, data: object) => {
                  if (!result) return;
                  const importData = data as CsvImportData;
                  if (importData.import_status !== 'success' || importData.ssv_fail === 1) {
                    console.error('CSV import failed or incomplete:', data);
                    return;
                  }
                  onImportSuccess({ rowCount: importData.row_count, rows: importData.rows });
                }}
                onClose={onClose}
                render={(launch, isLoading) => (
                  <Button
                    text='Choose CSV file'
                    size="large"
                    startIcon={<UploadIcon sx={{ mr: 1 }} />}
                    disabled={isLoading}
                    onClick={launch}
                  />
                )}
              />
            }
          </Box>
        </Box>

        <Box>
          <Typography variant='body2' sx={{
            fontWeight: 600
          }}>Common issues to watch for</Typography>
          <Grid container spacing={1} sx={{ mt: 1 }}>
            {COMMON_ISSUES.map((issue) => (
              <Grid size={{ xs: 12, sm: 6 }} key={issue}>
                <Typography variant='body2' sx={{
                  color: 'text.secondary'
                }}>• {issue}</Typography>
              </Grid>
            ))}
          </Grid>
        </Box>

      </Box>
    </Paper>
  );
};

export default UploadCsvStep;
